import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Tag,
  Gift,
  Stamp,
  MinusCircle,
  CalendarCheck,
  TrendingUp,
  TrendingDown,
  UserPlus,
  Users,
  Package,
  Hourglass,
  PartyPopper,
  CalendarClock,
  CalendarX,
  ShieldAlert,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import { getStoreUserActivity } from "@/lib/api/admin";
import type { UserActivity, UserActivityAction } from "@/lib/api/types";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/Spinner";
import { EmptyState } from "@/components/EmptyState";
import { formatDateTime } from "@/lib/utils/date";
import { formatInt, formatSigned } from "@/lib/utils/number";

const PAGE_SIZE = 20;

const ACTION_CONFIG: Record<
  UserActivityAction,
  { label: string; Icon: LucideIcon; color: string }
> = {
  item_claimed: { label: "Item claimed", Icon: Tag, color: "text-blue-500" },
  point_reward_redeemed: { label: "Point reward redeemed", Icon: Gift, color: "text-purple-500" },
  stamp_earned: { label: "Stamp earned", Icon: Stamp, color: "text-green-600" },
  stamp_reward_redeemed: {
    label: "Stamp reward redeemed",
    Icon: MinusCircle,
    color: "text-orange-500",
  },
  points_earned: { label: "Points earned", Icon: TrendingUp, color: "text-green-600" },
  points_spent: { label: "Points spent", Icon: TrendingDown, color: "text-orange-500" },
  event_checkin: { label: "Event self check-in", Icon: CalendarCheck, color: "text-indigo-500" },
  event_inviter_rewarded: {
    label: "Event guest check-in",
    Icon: UserPlus,
    color: "text-green-600",
  },
  referral_invitee_rewarded: {
    label: "Referral invitee bonus",
    Icon: UserPlus,
    color: "text-teal-500",
  },
  referral_inviter_rewarded: {
    label: "Referral inviter bonus",
    Icon: Users,
    color: "text-teal-500",
  },
  store_referral_invitee_rewarded: {
    label: "Store referral invitee bonus",
    Icon: UserPlus,
    color: "text-teal-500",
  },
  store_referral_inviter_rewarded: {
    label: "Store referral inviter bonus",
    Icon: Users,
    color: "text-teal-500",
  },
  bundle_items_claimed: { label: "Bundle claimed", Icon: Package, color: "text-blue-500" },
  points_expired: { label: "Points expired", Icon: Hourglass, color: "text-gray-500" },
  stamps_expired: { label: "Stamps expired", Icon: Hourglass, color: "text-gray-500" },
  signup_bonus: { label: "Welcome bonus", Icon: PartyPopper, color: "text-green-600" },
  appointment_checkin: {
    label: "Appointment check-in",
    Icon: CalendarClock,
    color: "text-indigo-500",
  },
  appointment_no_show_penalty: {
    label: "No-show penalty",
    Icon: CalendarX,
    color: "text-red-500",
  },
  admin_balance_adjusted: {
    label: "Balance adjusted by support",
    Icon: ShieldAlert,
    color: "text-red-500",
  },
};

// `fallback` (the action's own label) covers cases with no specific item/reward/event name.
function getItemName(activity: UserActivity, fallback: string): string {
  const { details } = activity;
  if (activity.action === "bundle_items_claimed") {
    const qty = details.total_quantity;
    return qty ? `Bundle (${qty} item${qty === 1 ? "" : "s"})` : "Bundle";
  }
  return (
    details.menu_item_name ??
    details.point_reward_name ??
    details.stamp_reward_name ??
    details.event_title ??
    fallback
  );
}

type ActivityTabProps = {
  storeId: string;
};

export function ActivityTab({ storeId }: ActivityTabProps) {
  const user = useAdminAuth();
  const [offset, setOffset] = useState(0);
  const [allItems, setAllItems] = useState<UserActivity[]>([]);
  const [total, setTotal] = useState<number | null>(null);

  const { data, isPending, isFetching } = useQuery({
    queryKey: ["store-activity", storeId, offset],
    queryFn: () => getStoreUserActivity(user!, storeId, { limit: PAGE_SIZE, offset }),
    enabled: !!user,
    refetchOnMount: "always",
  });

  // Synced from `data`, not inside queryFn, so a cache hit on remount still repopulates state.
  useEffect(() => {
    if (!data) return;
    if (offset === 0) {
      setAllItems(data.data);
    } else {
      setAllItems((prev) => [...prev, ...data.data]);
    }
    setTotal(data.total);
  }, [data, offset]);

  function handleLoadMore() {
    setOffset((prev) => prev + PAGE_SIZE);
  }

  const hasMore = total !== null && allItems.length < total;

  return (
    <div className="space-y-4">
      {total !== null && allItems.length > 0 && (
        <p className="text-sm text-muted-foreground">{formatInt(total)} total transactions</p>
      )}

      {isPending && allItems.length === 0 ? (
        <div className="flex justify-center py-8">
          <Spinner className="size-5 border-primary" />
        </div>
      ) : allItems.length === 0 ? (
        <EmptyState
          title="No activity yet"
          description="Customer transactions at this store will appear here."
        />
      ) : (
        <div className="rounded-lg border divide-y">
          {allItems.map((activity) => (
            <ActivityRow key={activity.id} activity={activity} />
          ))}
        </div>
      )}

      {hasMore && (
        <div className="flex justify-center pt-2">
          <Button variant="outline" size="sm" onClick={handleLoadMore} disabled={isFetching}>
            {isFetching ? (
              <>
                <Spinner className="size-4 border-primary mr-2" />
                Loading...
              </>
            ) : (
              "Load more"
            )}
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Local sub-component (single call site — kept in this file) ───────────────

function ActivityRow({ activity }: { activity: UserActivity }) {
  const config = ACTION_CONFIG[activity.action] ?? {
    label: activity.action,
    Icon: Tag,
    color: "text-muted-foreground",
  };
  const { Icon, color, label } = config;
  const itemName = getItemName(activity, label);
  // Skip repeating the label below when the primary line already fell back to it.
  const showLabel = itemName !== label;

  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <div className={`shrink-0 rounded-full bg-muted p-2 ${color}`}>
        <Icon className="size-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{itemName}</p>
        {showLabel && <p className="text-xs text-muted-foreground">{label}</p>}
        {activity.scanned_by_name && (
          <p className="text-xs text-muted-foreground truncate">
            Staff: {activity.scanned_by_name}
          </p>
        )}
        {activity.client_name && (
          <p className="text-xs text-muted-foreground truncate">Customer: {activity.client_name}</p>
        )}
      </div>
      <div className="shrink-0 text-right text-sm font-medium">
        {activity.points_change !== 0 && (
          <p className={activity.points_change > 0 ? "text-green-600" : "text-red-500"}>
            {formatSigned(activity.points_change)} pts
          </p>
        )}
        {activity.stamps_change !== 0 && (
          <p className={activity.stamps_change > 0 ? "text-green-600" : "text-red-500"}>
            {formatSigned(activity.stamps_change)} stamps
          </p>
        )}
        <p className="text-xs font-normal text-muted-foreground">
          {formatDateTime(activity.created_at)}
        </p>
      </div>
    </div>
  );
}
